import { FormEvent, KeyboardEventHandler, useRef, useState } from "react";
import cx from "classnames";

import { Editor, EditorHandle } from "../Editor";
import { useMentions } from "../../hooks/useMentions";
import {
  AtMentionSearchTab,
  DEFAULT_AT_MENTION_SEARCH_TAB,
  TriggerConfig,
} from "../../types";

import styles from "./styles.module.scss";

export interface ChatInputProps {
  triggers: TriggerConfig[];
  onSubmit: (message: string) => void;
  maskConfig?: boolean;
  /** Controlled tab for @-mention search (deal room). */
  atMentionTab?: AtMentionSearchTab;
  onAtMentionTabChange?: (tab: AtMentionSearchTab) => void;
}

export default function ChatInput({
  triggers,
  onSubmit,
  maskConfig = false,
  atMentionTab,
  onAtMentionTabChange,
}: ChatInputProps) {
  const editorRef = useRef<EditorHandle>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [hasText, setHasText] = useState(false);

  const resolvedAtMentionTab = atMentionTab ?? DEFAULT_AT_MENTION_SEARCH_TAB;

  const {
    items,
    activeIndex,
    coords,
    activeTrigger,
    hasMore,
    handleInput,
    handleKeyDown,
    handleSelect,
    isLoading,
    isLoadingMore,
    loadMoreAtMentionSuggestions,
    onDismiss,
    refetchAtMentionSuggestions,
  } = useMentions(triggers, editorRef, { atMentionTab: resolvedAtMentionTab });

  const activeConfig = triggers.find((t) => t.char === activeTrigger);
  const ActivePopover = activeConfig?.PopoverComponent;

  const onInput = () => {
    const root = editorRef.current?.getRoot();
    setHasText((root?.textContent || "").trim() !== "");
    handleInput?.();
  };

  const handleSend = (event: FormEvent) => {
    event.preventDefault();

    const root = editorRef.current?.getRoot();
    if (!root) return;

    // Post-processing: serialize mentions to string notation
    const clonedRoot = root.cloneNode(true) as HTMLElement;

    // Find all mention spans
    const mentionNodes = Array.from(clonedRoot.querySelectorAll('span'));

    mentionNodes.forEach(span => {
      // It's a mention if it has a dataset identifier and mention type OR original variable
      if (span.dataset.identifier && span.dataset.mentionType) {
        // e.g. @[people:1:Angela Duckworth]
        const label = span.textContent?.replace('@', '').trim() || '';
        const serialized = `@[${span.dataset.mentionType}:${span.dataset.identifier}:${label}]`;
        const textNode = document.createTextNode(serialized);
        span.replaceWith(textNode);
      }
    });

    const serializedMessage = clonedRoot.innerText || clonedRoot.textContent || "";

    if (serializedMessage.trim() !== '') {
      onSubmit(serializedMessage);
    }

    // reset editor after sending a message
    root.innerHTML = "";
    setHasText(false);
  }

  const onKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.code === 'Enter' && event.shiftKey === false) {
      event.preventDefault();
      formRef?.current?.requestSubmit();
      handleKeyDown?.(event);
    }
  }

  return (
    <div className={styles.chatInputContainer}>
      <form ref={formRef} onSubmit={handleSend}>
        <div
          className={cx(styles.inputContainer, {
            [styles.hideInputContainer]: maskConfig,
          })}
        >
          <div className={cx(styles.noPadding)}>
            <div className={styles.search}>
              <div className={styles.label}>
                <div className={styles.field}>
                  <Editor
                    ref={editorRef}
                    onInput={onInput}
                    onKeyDown={onKeyDown}
                  />
                </div>

                {activeTrigger && ActivePopover && (
                  <ActivePopover
                    items={items}
                    activeIndex={activeIndex}
                    coords={coords}
                    onSelect={handleSelect}
                    isLoading={activeTrigger === "@" ? isLoading : activeConfig?.isLoading ?? false}
                    isLoadingMore={isLoadingMore}
                    hasMore={activeTrigger === "@" ? hasMore : false}
                    onLoadMore={activeTrigger === "@" ? loadMoreAtMentionSuggestions : undefined}
                    onDismiss={onDismiss}
                    getMentionAnchorRoot={() => editorRef.current?.getRoot() ?? null}
                    mentionTab={atMentionTab}
                    onMentionTabChange={(tab) => {
                      onAtMentionTabChange?.(tab);
                      refetchAtMentionSuggestions(tab);
                    }}
                  />
                )}
                <button
                  type="submit"
                  className={cx(styles.searchButton, {
                    [styles.searchButtonActive]: hasText,
                  })}
                >
                  <i className="paperflite-right-arrow" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>

  )
}